import React, { useEffect, useState, useRef } from 'react';
import { View, Text, ScrollView, TextInput } from 'react-native';
import {
    ActivityIndicator,
    Button,
    Hr,
    InfoBox,
    InputWithIconButton,
    CustomKeyboardAvoidingView
} from 'LocalGluons';
// import { bool, func } from 'prop-types';
import { t } from 'Locale';
import theme from 'Theme';
import { useStore } from 'Store';
import { Device } from 'DeviceUtils';

import { ResponseBase } from '@24i/st-api-connector/interface/types';
import pageStyles, { buttonStyle } from './PageStyles.jss';

interface ChangePasswordProps {
    onSaved: () => void;
    onCancel: () => void;
    displayHeader?: boolean;
}

const ChangePassword = ({ onSaved, onCancel, displayHeader = true }: ChangePasswordProps) => {
    const { appManager } = useStore();
    const [currentPassword, setCurrentPassword] = useState('');
    const [password, setPassword] = useState('');
    const [passwordRepeat, setPasswordRepeat] = useState('');
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [secureIconPressed, setSecureIconPressed] = useState(false);
    const newPasswordRef = useRef<TextInput | null>(null);
    const newPasswordConfirmRef = useRef<TextInput | null>(null);

    const onSecureIconPressed = (state) => {
        setSecureIconPressed(state);
    };

    useEffect(() => {
        setErrorMessage(null);
    }, []);

    const savePassword = async () => {
        if (currentPassword.trim().length <= 0) {
            setErrorMessage(t('current_password_empty'));
            return;
        }
        if (password.length < 6) {
            setErrorMessage(t('password_short'));
            return;
        }
        if (password !== passwordRepeat) {
            setErrorMessage(t('COMMON.PASSWORD_MUST_MATCH'));
            return;
        }

        setLoading(true);
        try {
            await appManager.api.setProfile({
                oldPassword: currentPassword,
                newPassword: password
            });
            onSaved();
        } catch (error) {
            const typedError = error as ResponseBase;
            setErrorMessage(typedError.localizedMessage);
        } finally {
            setLoading(false);
        }
    };

    const onCurrentPasswordSubmit = () => {
        newPasswordRef.current?.focus();
    };

    const onNewPasswordSubmit = () => {
        newPasswordConfirmRef.current?.focus();
    };

    return (
        <CustomKeyboardAvoidingView>
            <ScrollView
                keyboardShouldPersistTaps="handled"
                contentContainerStyle={pageStyles.container}
            >
                {displayHeader && (
                    <>
                        <Text style={pageStyles.title}>{t('SETTINGS.CHANGE_PASSWORD')}</Text>
                        <Hr />
                    </>
                )}
                {errorMessage && <InfoBox style={pageStyles.infoBox} message={errorMessage} />}
                <View style={pageStyles.firstButtonMargin} />

                <View style={pageStyles.textinputConteiner}>
                    <InputWithIconButton
                        secureTextEntry
                        style={pageStyles.input}
                        placeholder={t('COMMON.CURRENT_PASSWORD')}
                        placeholderTextColor={theme.placeholderTextColor}
                        onChangeText={setCurrentPassword}
                        value={currentPassword}
                        returnKeyType="next"
                        onSubmitEditing={onCurrentPasswordSubmit}
                        testID="current_password_input"
                    />
                </View>
                <View style={pageStyles.firstButtonMargin} />

                <View style={pageStyles.textinputConteiner}>
                    <InputWithIconButton
                        setRef={(ref) => {
                            newPasswordRef.current = ref;
                        }}
                        secureTextEntry
                        style={pageStyles.input}
                        placeholder={t('COMMON.NEW_PASSWORD')}
                        placeholderTextColor={theme.placeholderTextColor}
                        onChangeText={setPassword}
                        value={password}
                        onSecureIconPress={onSecureIconPressed}
                        returnKeyType="next"
                        onSubmitEditing={onNewPasswordSubmit}
                        testID="new_password_input"
                    />
                </View>
                <View style={pageStyles.textinputConteiner}>
                    <InputWithIconButton
                        setRef={(ref) => {
                            newPasswordConfirmRef.current = ref;
                        }}
                        secureTextEntry
                        style={pageStyles.input}
                        placeholder={t('COMMON.NEW_PASSWORD_AGAIN')}
                        placeholderTextColor={theme.placeholderTextColor}
                        onChangeText={setPasswordRepeat}
                        value={passwordRepeat}
                        showText={secureIconPressed}
                        returnKeyType="done"
                        testID="new_password_confirm_input"
                    />
                </View>
                <Text style={pageStyles.grayText} numberOfLines={10}>
                    {t('SETTINGS.PASSWORD_REQUISITES')}
                </Text>
                <View style={(pageStyles.buttonsContainer, pageStyles.buttonWidth)}>
                    <Button
                        style={buttonStyle}
                        title={t('SETTINGS.SAVE_NEW_PASSWORD')}
                        onPress={savePassword}
                        testID="save_password_button"
                    />
                    {Device.isTabletOrWeb ? null : (
                        <Button
                            style={buttonStyle}
                            title={t('COMMON.CANCEL')}
                            onPress={onCancel}
                            isSecondary
                            testID="cancel_button"
                        />
                    )}
                </View>
                {loading && <ActivityIndicator isOverlay />}
            </ScrollView>
        </CustomKeyboardAvoidingView>
    );
};

// ChangePassword.defaultProps = {
//     displayHeader: true
// };

// ChangePassword.propTypes = {
//     onSaved: func.isRequired,
//     onCancel: func.isRequired,
//     displayHeader: bool
// };

export default ChangePassword;
